import React, { forwardRef } from 'react';
import { Grid } from '@material-ui/core';
import { observer } from 'mobx-react';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';

import useSession from 'core/hooks/use-session';
import { useQuery, gql } from '@apollo/client';
import { useStore } from 'core/store/store.mobx';
import { MyCommunity } from '../my-community';
import { Pledges } from '../pledges';
import { CustomerAdditionalOffersSection } from '../customer-additional-offers-section';
import { AccountDetails } from '../account-details';
import { AgentDetails } from '../agent-details';
import { MarketTierCard } from '../market-tier-card';
import { checkIsFarmEnvironment } from 'common/helpers/environment-helpers';
import { isBranchEnv } from 'brand-config';
import { buildBixAutoUpsellInput } from 'common/components/branch-auto-upsell';
import { SidebarLayoutToggle, SortableSidebar, useCustomSidebarLayout } from 'common/components/sortable-sidebar';
import { isPocEnabled } from 'core/pocs';
import { SORTABLE_SIDEBARS_POC } from 'core/pocs/entries/sortable-sidebars';
import useStyles from './sidebar.styles';

const isFarmEnvironment = checkIsFarmEnvironment();

// Keeps the theme's Grid item padding, so sortable cards line up with the default sidebar
const SortableGridItem = forwardRef((props, ref) => <Grid item ref={ref} {...props} />);

const CustomerSidebar = observer((props) => {
  const {
    account,
    account: { policies, marketTierRating, accountLoading }
  } = useStore();
  const recentPolicy = policies.getRecentPolicy(); // Using getRecentPolicy() here as per discussion in issue #21742 - life insurance eligibility doesn't require an active policy

  const classes = useStyles();
  const [useCustomLayout, toggleCustomLayout] = useCustomSidebarLayout();
  const { canSeeMarketTierCard, viewOnly } = useSession();

  const assurityLifeEligibilityQuery = useQuery(
    gql`
      query ($policyId: String) {
        assurityLifeCheckEligibility(args: { branchPolicyId: $policyId }) {
          __typename
          ... on AssurityLifeCheckEligibilitySuccessResponse {
            staffUserCapabilities
            eligibleLifeOffers {
              personId
              benefitAmount
              monthlyPremiumAmount
              annualPremiumAmount
            }
          }
        }
      }
    `,
    { variables: { policyId: recentPolicy?.id }, skip: !recentPolicy?.id }
  );

  const shouldShowAssurityTermLifeAdditionalOffer = Boolean(
    !assurityLifeEligibilityQuery.loading &&
      assurityLifeEligibilityQuery.data?.assurityLifeCheckEligibility.__typename !==
        'AssurityLifeCheckEligibilityStaffUserNotEligibleResponse' &&
      !isFarmEnvironment
  );

  // BIX auto upsell — only on non-Branch carrier builds when the recent policy is a
  // farm policy. Gated on `!viewOnly` so view-only operators don't trigger the
  // mount-time mutation. (We can't use `canEditPolicies` here — that's a Branch-staff
  // role check; mutual carrier agents aren't in those groups.) `isFarmBasedPolicyType`
  // covers all farm policy types (F/Z/Y/FA). Applicant name comes from the account;
  // address from `offer.quote.correctedAddress` — the post-validation policyholder
  // address attached to the originating offer, which is what BIX rates against.
  // Agent eligibility (`branch_play = "Mutuals Accommodation"`) lives server-side in
  // the create-bix-auto-offer-upsell lambda — see issue #27759.
  const bixAutoUpsellInput =
    !viewOnly && !isBranchEnv && isFarmBasedPolicyType(recentPolicy?.policyType) && recentPolicy?.id
      ? buildBixAutoUpsellInput({
          branchPolicyId: recentPolicy.id,
          applicant: { firstName: account.fname, lastName: account.lname },
          farmAddress: recentPolicy?.offer?.quote?.correctedAddress,
          email: account.email,
          phone: account.phoneNumber
        })
      : null;

  const shouldShowAdditionalOffers = shouldShowAssurityTermLifeAdditionalOffer || !!bixAutoUpsellInput;

  const cards = [
    {
      id: 'AccountDetails',
      title: 'Account Details',
      show: true,
      className: classes.bottomSpacing,
      node: <AccountDetails {...props} />
    },
    {
      id: 'MarketTier',
      title: 'Target Market Tier',
      show: !!marketTierRating && canSeeMarketTierCard && !isFarmEnvironment,
      className: classes.gridItem,
      node: <MarketTierCard {...props} marketTierRating={marketTierRating} accountLoading={accountLoading} />
    },
    {
      id: 'AgentDetails',
      title: 'Agent Details',
      show: true,
      className: classes.gridItem,
      node: <AgentDetails {...props} />
    },
    {
      id: 'AdditionalOffers',
      title: 'Additional Offers',
      show: shouldShowAdditionalOffers,
      className: classes.gridItem,
      node: (
        <CustomerAdditionalOffersSection
          assurityTermLife={shouldShowAssurityTermLifeAdditionalOffer}
          assurityTermLifeData={{
            ...assurityLifeEligibilityQuery?.data?.assurityLifeCheckEligibility,
            email: account.email
          }}
          bixAutoUpsellInput={bixAutoUpsellInput}
        />
      )
    },
    {
      id: 'MyCommunity',
      title: 'My Community',
      show: !isFarmEnvironment,
      className: classes.gridItem,
      node: <MyCommunity {...props} />
    },
    {
      id: 'Pledges',
      title: 'Pledges',
      show: !isFarmEnvironment,
      className: classes.gridItem,
      node: <Pledges {...props} />
    }
  ];

  const defaultSidebar = (
    <Grid container>
      {cards
        .filter((card) => card.show)
        .map((card) => (
          <Grid item key={card.id} className={card.className}>
            {card.node}
          </Grid>
        ))}
    </Grid>
  );

  if (!isPocEnabled(SORTABLE_SIDEBARS_POC)) {
    return defaultSidebar;
  }

  return (
    <>
      <SidebarLayoutToggle
        useCustomLayout={useCustomLayout}
        onToggle={toggleCustomLayout}
        className={classes.layoutToggle}
      />
      {useCustomLayout ? (
        <Grid container>
          <SortableSidebar name="customer" cards={cards} itemComponent={SortableGridItem} />
        </Grid>
      ) : (
        defaultSidebar
      )}
    </>
  );
});

export default CustomerSidebar;
